import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useContext, useEffect, useMemo } from "react";
import StateProductContext from "../StageProductContext";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import useGetTransferTrucksLazy from "hooks/organization/useGetTransferTrucksLazy";
import PlantProductTransferTrucksTableBody from "./PlantProductTransferTrucksTableBody";
import {
  ProductMeasurementJob,
  ProductProductionRateType,
  ProductType,
  ProposalPlantProduct,
} from "generated/graphql";
import { snakeCase } from "change-case";
import ProposalContext from "modules/proposals/providers/ProposalContextProvider";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";
import toFiniteNumber from "utils/number/toFiniteNumber";

interface PlantProductTruckingTableProps {
  proposalPlantProduct: ProposalPlantProduct;
  productMaterialNeeded: number;
  rateOnWeight?: boolean;
  showAdvancedColumns?: boolean;
}

function PlantProductTruckingTable({
  proposalPlantProduct,
  productMaterialNeeded,
  rateOnWeight = false,
  showAdvancedColumns = false,
}: PlantProductTruckingTableProps) {
  const { product } = useContext(StateProductContext);
  const {
    proposal: {
      organization: { proposalDefaults },
    },
  } = useContext(ProposalContext);
  const { organizationSettings } = useContext(ProposalFormsContext);
  const theme = useTheme();
  const compactMode = useMediaQuery(theme.breakpoints.down("xl"));
  const [getTransferTrucks, { transferTrucks: orgTransferTrucks, loading }] =
    useGetTransferTrucksLazy();
  const averageWorkingHoursPerDay = +(organizationSettings?.["average_working_hours_per_day"] ?? 0);

  const jobDurationInDays = useMemo(() => {
    const jobDuration = product.jobDurationOverride ?? product.jobDuration;
    return product.jobDurationDaysOverride ?? +jobDuration / +averageWorkingHoursPerDay;
  }, [product, averageWorkingHoursPerDay]);

  useEffect(() => {
    getTransferTrucks();
  }, []);

  const transferTrucks = proposalPlantProduct?.transferTrucks || [];

  const proposalPlantProductWithRateOnWeight = useMemo(
    () => ({ ...proposalPlantProduct, rateOnWeight }),
    [proposalPlantProduct, rateOnWeight]
  );

  const transferTrucksFields = useMemo(() => {
    return orgTransferTrucks.map((truck) => {
      const productTruck = transferTrucks.find((t) => t.transferTruckId === truck.id);
      const rateColumn = rateOnWeight ? "rateTon" : "rateHourly";
      const defaultRateColumn = rateOnWeight ? "defaultRateTon" : "defaultRateHourly";

      const tonnage = productTruck?.tonnage || truck.defaultTonnage;
      const dailyUnitsComplete =
        product.material.dailyUnitsCompleteOverride || product.material.dailyUnitsComplete;
      let productCompletionRatePerHour =
        +dailyUnitsComplete > 0 && averageWorkingHoursPerDay > 0
          ? +dailyUnitsComplete / averageWorkingHoursPerDay
          : 0;
      if (product.material.productionRateType === ProductProductionRateType.TONS_PER_HOUR) {
        productCompletionRatePerHour = +dailyUnitsComplete;
      }
      const defaultProductionRate = Math.round(toFiniteNumber(productCompletionRatePerHour));
      let truckingProductionRate = toFiniteNumber(
        productTruck?.truckingProductionRate,
        defaultProductionRate
      );
      if (
        !productTruck?.truckingProductionRate &&
        product.material.productionRateType !== ProductProductionRateType.TONS_PER_HOUR
      ) {
        if (product.material.measurementJob === ProductMeasurementJob.SQUARE_YARD) {
          truckingProductionRate = Math.round(
            toFiniteNumber(product.material.materialNeeded / product.jobDuration)
          );
        } else {
          truckingProductionRate = Math.round(
            toFiniteNumber(product.material.materialNeeded / product.jobDuration)
          );
        }

        // for slurry and potentially microsurfacing https://app.clickup.com/t/8692mqk9h
        // if (
        //   product.material.type === snakeCase(ProductType.SLURRY_SEAL) ||
        //   product.material.type === snakeCase(ProductType.MICROSURFACING)
        // ) {
        //   const applicationRate =
        //     // @ts-ignore
        //     product.material.attributesOverride?.applicationRate ||
        //     // @ts-ignore
        //     product.material.attributes?.applicationRate;
        //   truckingProductionRate =
        //     (+dailyUnitsComplete * applicationRate) / 2000 / (averageWorkingHoursPerDay || 1);
        // }
      }
      truckingProductionRate = toFiniteNumber(truckingProductionRate);
      return {
        name: truck.name,
        quantity: productTruck?.quantity || 0,
        quantityOverride: productTruck?.quantityOverride || null,
        transferTruckId: truck.id,
        rate: productTruck?.[rateColumn] || truck[defaultRateColumn],
        rateOverride: productTruck?.[`${rateColumn}Override`] || null,
        totalHaulingHours: productTruck?.totalHaulingHours || null,
        totalHaulingHoursOverride: productTruck?.totalHaulingHoursOverride || null,
        costOverride: productTruck?.costOverride || null,
        cost: productTruck?.cost || null,
        tonnage,
        truckingProductionRate,
        productTransferTruckId: productTruck?.id || null,
        proposalPlantProductId: proposalPlantProduct.id,
      };
    });
  }, [orgTransferTrucks, proposalPlantProduct, transferTrucks, product.material, rateOnWeight]);

  const totalCostForPlant = Array.isArray(transferTrucks)
    ? transferTrucks.reduce((acc, truck) => {
        return acc + (truck.costOverride ?? truck.cost ?? 0);
      }, 0)
    : 0;
  const totalColumns = showAdvancedColumns ? 11 : 9;
  const columnWidths = showAdvancedColumns
    ? {
        truck: "10%",
        tons: "7%",
        rate: "9%",
        tonsPerHour: "9%",
        trucks: "8%",
        hoursPerTruck: "10%",
        haulingHours: "10%",
        trips: "8%",
        tripsPerTruck: "8%",
        cost: "10%",
        actions: "5%",
      }
    : compactMode
    ? {
        truck: "12%",
        tons: "8%",
        rate: "10%",
        tonsPerHour: "10%",
        trucks: "10%",
        hoursPerTruck: "12%",
        haulingHours: "12%",
        cost: "12%",
        actions: "6%",
      }
    : {
        truck: "12%",
        tons: "8%",
        rate: "10%",
        tonsPerHour: "10%",
        trucks: "10%",
        hoursPerTruck: "12%",
        haulingHours: "12%",
        cost: "12%",
        actions: "6%",
      };

  return (
    <>
      <TableContainer component={Paper} sx={{ paddingBottom: 3, pr: 0.5 }}>
        <Table
          sx={{
            width: "98%",
            mx: 0,
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              py: 0.5,
              px: compactMode ? 0.35 : 0.6,
              fontSize: compactMode ? "0.72rem" : "0.78rem",
            },
          }}
          aria-label="simple table"
        >
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell sx={{ width: columnWidths.truck }}>Truck</TableCell>
              <TableCell align="center" sx={{ width: columnWidths.tons }}>
                Tons
              </TableCell>
              <TableCell align="center" sx={{ width: columnWidths.rate }}>
                Rate
              </TableCell>
              <TableCell align="center" sx={{ width: columnWidths.tonsPerHour }}>
                Tons per Hour
              </TableCell>
              <TableCell align="center" sx={{ width: columnWidths.trucks }}>
                Trucks
              </TableCell>
              <TableCell align="center" sx={{ width: columnWidths.hoursPerTruck }}>
                Hours per
                <br />
                Truck
              </TableCell>
              <TableCell align="center" sx={{ width: columnWidths.haulingHours }}>
                Hauling Hours
              </TableCell>
              {showAdvancedColumns && (
                <>
                  <TableCell align="center" sx={{ width: columnWidths.trips }}>
                    Trips
                  </TableCell>
                  <TableCell align="center" sx={{ width: columnWidths.tripsPerTruck }}>
                    Trips per
                    <br />
                    Truck
                  </TableCell>
                </>
              )}
              {rateOnWeight && (
                <TableCell align="center" sx={{ width: columnWidths.cost }}>
                  Cost
                </TableCell>
              )}
              {!rateOnWeight && (
                <TableCell align="center" sx={{ width: columnWidths.cost }}>
                  Cost
                </TableCell>
              )}
              <TableCell align="center" sx={{ width: columnWidths.actions }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && (
              <PlantProductTransferTrucksTableBody
                transferTrucksFields={transferTrucksFields}
                product={product}
                proposalPlantProduct={{
                  ...proposalPlantProduct,
                  rateOnWeight,
                }}
                productTransferTrucks={transferTrucks}
                averageWorkingHoursPerDay={averageWorkingHoursPerDay}
                proposalDefaults={proposalDefaults}
                jobDurationInDays={jobDurationInDays}
                compactMode={compactMode}
                showAdvancedColumns={showAdvancedColumns}
              />
            )}

            <TableRow>
              <TableCell colSpan={totalColumns} sx={{ padding: 0, borderBottom: "none" }}>
                <div style={{ height: 16 }} />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ color: "#555" }}>Trucking Cost</TableCell>
              <TableCell align="right" sx={{ color: "#555" }}></TableCell>
              <TableCell align="right" sx={{ color: "#555" }}>
                {formatCentsToUSD(totalCostForPlant)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ color: "#555" }}>Material Cost</TableCell>
              <TableCell align="right" sx={{ color: "#555" }}></TableCell>
              <TableCell align="right" sx={{ color: "#555" }}>
                {formatCentsToUSD(proposalPlantProduct.costMaterial)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default PlantProductTruckingTable;
